import { useRef } from "react";

import { Table as Base } from "@corca/tiptap";
import { Plugin } from "@tiptap/pm/state";
import { columnIsHeader, rowIsHeader, TableMap } from "@tiptap/pm/tables";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  findParentNodeClosestToPos,
  useReactNodeView,
} from "@tiptap/react";

import {
  appendColumn,
  addColumnAfterWithHeaderAttrs,
  appendRow,
  popColumn,
  popRow,
  goToNextCellVertical,
  setHeaderTextStyle,
  setHeaderTextAlignment,
  addRowAfterWithHeaderAttrs,
  setColumnWidth,
  setRowHeight,
  setHeaderAttrs,
} from "@/components/tiptap/tables/commands";
import { useColumnResize } from "@/components/tiptap/tables/resize/useColumnResize";
import { useRowResize } from "@/components/tiptap/tables/resize/useRowResize";
import { getCellPosition, getTableStyleProps, isCellLike } from "@/components/tiptap/tables/utils";
import { LineWrapper } from "@/components/tiptap/utils/line-wrapper/LineWrapper";
import { twMerge } from "@/utils/twMerge";

import { isCellSelection } from "../utils/isCellSelection";

import type { Alignment } from "@/components/doc/right-sidebar/align-dropdown/AlignDropdown";
import type { HeaderType } from "@/components/tiptap/tables/commands";
import type { TableAttrs } from "@/components/tiptap/tables/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { FontSize, Color } from "@corca/data";
import type { TableOptions } from "@tiptap/extension-table";
import type { Attrs, Node } from "@tiptap/pm/model";
import type { Direction } from "@tiptap/pm/tables";
import type { NodeViewProps } from "@tiptap/react";

type PluginState = {
  decorations: DecorationSet | null;
  node: Node | null;
  pos: number | null;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableExtended: {
      /**
       * Append rows to the end of the table
       * @param rows
       */
      appendRow(rows: number): ReturnType;
      /**
       * Remove rows from the end of the table
       * @param rows
       */
      popRow(rows: number): ReturnType;
      /**
       * Append columns to the end of the table
       * @param cols
       */
      appendColumn(cols: number): ReturnType;
      /**
       * Add a column after the current column and copy the header cell attributes
       * @param keys - The keys of the attributes to copy from the header cell
       */
      addColumnAfterWithHeaderAttrs(keys?: string[]): ReturnType;
      /**
       * Add a row below the current row and copy the header cell attributes
       * @param keys - The keys of the attributes to copy from the header cell
       */
      addRowAfterWithHeaderAttrs(keys?: string[]): ReturnType;
      /**
       * Remove columns from the end of the table
       * @param cols
       */
      popColumn(cols: number): ReturnType;
      /**
       * Move the focus to the next cell in the specified direction
       * @param direction
       */
      goToNextCellVertical(direction: Direction): ReturnType;
      /**
       * Set the font size of the header row
       * @param size
       * @param type
       */
      setHeaderFontSize(size: FontSize, type?: HeaderType): ReturnType;
      /**
       * Set the font color of the header row
       * @param color
       * @param type
       */
      setHeaderFontColor(color: Color, type?: HeaderType): ReturnType;
      /**
       * Set the alignment of the header row
       * @param alignment
       * @param type
       */
      setHeaderTextAlignment(alignment: Alignment, type?: HeaderType): ReturnType;
      /**
       * Set the width of the current table column
       * @param width
       * @param cellPos - Optional position of the cell to set the width for. If not provided, it will use the current selection to find the cell.
       */
      setColumnWidth: (width: number | null, cellPos?: number) => ReturnType;
      /**
       * Set the height of the current table row
       * @param height
       * @param rowPos - Optional position of the row to set the height for. If not provided, it will use the current selection to find the row.
       */
      setRowHeight: (height: number | null, rowPos?: number) => ReturnType;
      /**
       * Set the attributes of the header row
       * @param attrs
       * @param type
       */
      setHeaderAttrs(attrs: Attrs, type?: HeaderType): ReturnType;
    };
  }
}

interface Props extends NodeViewProps {
  cellMinWidth?: number;
}

type TableExtensionOptions = {
  doc: DocumentModel;
};

function TableLineWrapper({ node, selected, cellMinWidth, extension, getPos, editor }: Props) {
  const { doc } = extension.options as TableExtensionOptions;
  // Using useReactNodeView directly instead of using NodeViewContent to be able to insert colgroup.
  // See https://github.com/ueberdosis/tiptap/blob/8df5b50905d14b4c0d5531c2c04324eaca506b2d/packages/react/src/NodeViewContent.tsx
  const { nodeViewContentRef, nodeViewContentChildren } = useReactNodeView();

  const { showHorizontalLines, showVerticalLines } = node.attrs as TableAttrs;
  const { tableStyle, colStyles } = getTableStyleProps(node, cellMinWidth);

  const tableMap = TableMap.get(node);
  const headerRowIsEnabled = rowIsHeader(tableMap, node, 0);
  const headerColumnIsEnabled = columnIsHeader(tableMap, node, 0);

  const resizeContainerRef = useRef<HTMLDivElement>(null);
  const columnResizeState = useColumnResize(resizeContainerRef, editor, node, getPos);
  const rowResizeState = useRowResize(resizeContainerRef, editor, node, getPos);

  return (
    <NodeViewWrapper>
      <LineWrapper
        ref={resizeContainerRef}
        doc={doc}
        getPos={getPos}
        contentClassName="overflow-y-hidden overflow-x-auto sm:overflow-x-auto w-full max-w-content-wrapper-width sm:max-w-full p-[1px] pb-[3px]"
      >
        <table
          role="grid"
          aria-readonly={!editor.isEditable}
          className={twMerge(
            "h-fit w-full table-fixed border-collapse whitespace-pre-wrap",
            showHorizontalLines &&
              "[&_td]:border-b [&_td]:border-b-gray-200 [&_th]:border-b [&_th]:border-b-gray-200",
            headerRowIsEnabled && "[&_tr:first-child>th]:border-b-document-text",
            selected && "bg-selected-cell-bg selection:bg-transparent",
          )}
          style={tableStyle}
          ref={nodeViewContentRef}
          data-node-view-content=""
        >
          {colStyles && colStyles.length > 0 && (
            <colgroup>
              {colStyles.map((style, index) => (
                <col
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  style={style}
                  className={twMerge(
                    "border-r border-r-transparent last:border-r-0",
                    showVerticalLines && "border-r-gray-200",
                    headerColumnIsEnabled && "first:border-r-document-text",
                  )}
                />
              ))}
            </colgroup>
          )}
          {nodeViewContentChildren}
        </table>
        {columnResizeState && (
          <div
            data-testid="column-resize-guide"
            className="absolute top-[-1px] w-[2px] bottom-0 -translate-x-[1px] bg-blue-500 pointer-events-none z-50"
            style={{
              left: columnResizeState.guideX,
            }}
          />
        )}
        {rowResizeState && (
          <div
            data-testid="row-resize-guide"
            className="absolute left-[-1px] h-[2px] right-0 -translate-y-[1px] bg-blue-500 pointer-events-none z-50"
            style={{
              top: rowResizeState.guideY,
            }}
          />
        )}
      </LineWrapper>
    </NodeViewWrapper>
  );
}

export const Table = Base.extend<TableOptions & { doc: DocumentModel }>({
  addNodeView() {
    return ReactNodeViewRenderer(
      (props) => {
        return <TableLineWrapper {...props} cellMinWidth={this.options.cellMinWidth} />;
      },
      {
        className: "w-full mx-auto max-w-content-width sm:max-w-full",
        contentDOMElementTag: "tbody",
      },
    );
  },

  addCommands() {
    return {
      ...this.parent?.(),
      addColumnAfter:
        () =>
        ({ commands }) => {
          return commands.addColumnAfterWithHeaderAttrs([
            "backgroundColor",
            "alignment",
            "verticalAlignment",
            "size",
            "color",
          ]);
        },
      addRowAfter:
        () =>
        ({ commands }) => {
          return commands.addRowAfterWithHeaderAttrs([
            "backgroundColor",
            "alignment",
            "verticalAlignment",
            "size",
            "color",
          ]);
        },
      addColumnAfterWithHeaderAttrs: (keys) => (props) => {
        return addColumnAfterWithHeaderAttrs(keys)(props);
      },
      addRowAfterWithHeaderAttrs: (keys) => (props) => {
        return addRowAfterWithHeaderAttrs(keys)(props);
      },
      appendRow: (rows) => (props) => {
        return appendRow(rows)(props);
      },
      popRow: (rows) => (props) => {
        return popRow(rows)(props);
      },
      appendColumn: (cols) => (props) => {
        return appendColumn(cols)(props);
      },
      popColumn: (cols) => (props) => {
        return popColumn(cols)(props);
      },
      goToNextCellVertical: (direction) => (props) => {
        return goToNextCellVertical(direction)(props);
      },
      setHeaderFontSize: (size, type) => (props) => {
        return setHeaderTextStyle({ size }, type)(props);
      },
      setHeaderFontColor: (color, type) => (props) => {
        return setHeaderTextStyle({ color }, type)(props);
      },
      setHeaderTextAlignment: (alignment, type) => (props) => {
        return setHeaderTextAlignment(alignment, type)(props);
      },
      setColumnWidth: (width, cellPos) => (props) => {
        const { selection } = props.state;
        const pos = cellPos ?? findParentNodeClosestToPos(selection.$head, isCellLike)?.pos;

        if (pos === undefined) {
          return false;
        }

        return setColumnWidth(pos, width)(props);
      },
      setRowHeight: (height, rowPos) => (props) => {
        const { selection } = props.state;
        const pos =
          rowPos ??
          findParentNodeClosestToPos(selection.$head, (node) => node.type.name === "tableRow")?.pos;

        if (pos === undefined) {
          return false;
        }

        return setRowHeight(pos, height)(props);
      },
      setHeaderAttrs: (attrs, type) => (props) => {
        return setHeaderAttrs(attrs, type)(props);
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),
      "Shift-ArrowUp": ({ editor }) => {
        const { selection } = editor.state;

        if (isCellSelection(selection)) {
          return false;
        }

        const { $anchor, $head } = selection;
        const isInsideTable = $head.node(1)?.type.name === "table";

        if (isInsideTable || $head.pos === 0 || $head.depth === 0) {
          return false;
        }

        const nodePos = editor.$pos($head.pos);

        if (nodePos.pos - nodePos.node.nodeSize <= 0) {
          return false;
        }

        const { before } = nodePos;

        if (before?.node && before.node.type.name === "table") {
          return editor.commands.setTextSelection({
            from: $anchor.pos,
            to: before.pos - before.node.nodeSize + 1,
          });
        }

        return false;
      },
      "Shift-ArrowDown": ({ editor }) => {
        const { selection } = editor.state;

        if (isCellSelection(selection)) {
          return false;
        }

        const { $anchor, $head } = selection;
        const isInsideTable = $head.node(1)?.type.name === "table";

        if (isInsideTable || $head.pos === 0 || $head.depth === 0) {
          return false;
        }

        const nodePos = editor.$pos($head.pos);
        const resolvedNodePos = editor.state.doc.resolve(nodePos.pos);

        // Workaround for out of range error
        if (resolvedNodePos.after(1) === editor.state.doc.content.size) {
          return false;
        }

        const { after } = nodePos;

        if (after?.node && after.node.type.name === "table") {
          return editor.commands.setTextSelection({
            from: $anchor.pos,
            to: after.pos + after.node.nodeSize,
          });
        }

        return false;
      },
    };
  },

  addProseMirrorPlugins() {
    const plugins = this.parent?.() ?? [];
    const focusedCellPlugin = new Plugin<PluginState>({
      state: {
        init: () => ({ decorations: null, node: null, pos: null }),
        apply(tr, state) {
          const cell = findParentNodeClosestToPos(tr.selection.$head, isCellLike);

          if (!cell) {
            return { decorations: null, node: null, pos: null };
          }

          if (state.node === cell.node && state.pos === cell.pos && !!state.decorations) {
            return state;
          }

          const decorations = [
            Decoration.node(cell.pos, cell.pos + cell.node.nodeSize, {
              class: "focused-cell",
            }),
          ];
          const position = getCellPosition(tr.doc, cell.pos);

          if (position) {
            decorations.push(
              Decoration.widget(cell.pos + 1, () => {
                const alignment = cell.node.attrs.alignment as Alignment;
                const el = document.createElement("div");

                el.className = twMerge(
                  "font-mono font-normal select-none text-gray-400 dark:text-gray-600 text-xxs absolute top-[1px] right-[2px]",
                  alignment === "right" && "left-[2px]",
                );
                el.contentEditable = "false";
                el.textContent = position.address;

                return el;
              }),
            );
          }

          return {
            node: cell.node,
            pos: cell.pos,
            decorations: DecorationSet.create(tr.doc, decorations),
          };
        },
      },
      props: {
        decorations(state) {
          return this.getState(state)?.decorations ?? null;
        },
      },
    });

    return [...plugins, focusedCellPlugin];
  },
});
